import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { IProgressStep, ProgressValues } from '@pc/progress-step-indicator';
import { NavigationService } from '@pc/shared';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ProgressStepperService {
  public progress: BehaviorSubject<ProgressValues> = new BehaviorSubject<ProgressValues>(ProgressValues.Zero);
  public progress$: Observable<ProgressValues> = this.progress.asObservable();
  public nextSubject: Subject<void> = new Subject<void>();
  public onNext$: Observable<void> = this.nextSubject.asObservable();
  public previousSubject: Subject<void> = new Subject<void>();
  public onPrevious$: Observable<void> = this.previousSubject.asObservable();
  public onNavigateToStepSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public onNavigateToStep$: Observable<number> = this.onNavigateToStepSubject.asObservable();
  public progressSteps: IProgressStep[] = [];
  public fullStepSet: IProgressStep[] = [];
  public selectedIndex = signal<number>(0);
  public currentStep = computed<IProgressStep>(() => this.progressSteps[this.selectedIndex()]);
  public navigationService = inject(NavigationService);
  public router = inject(Router);
  public previousUrl = '';
  public currentUrl = '';

  constructor() {
    this.currentUrl = this.router.url;
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.previousUrl = this.currentUrl;
        this.currentUrl = event.url;
      }
    });
    effect(() => {
      this.updateCompletedSteps();
    });
  }

  public setProgressSteps(steps: IProgressStep[]): void {
    this.fullStepSet = steps;
    this.progressSteps = this.fullStepSet.filter((step) => !step.isCustomStep);
  }

  public emitSelectedIndexUpdate(index: number): void {
    this.selectedIndex.set(index);
  }

  public updateProgress(progress: ProgressValues): void {
    this.progress.next(progress);
  }

  public dynamicStepNavigation(): void {
    const currentStepIndex = this.findCurrentStepIndex();
    if (currentStepIndex < 0) return;
    const currentStep = this.fullStepSet[currentStepIndex];
    const targetIndex = this.calculateTargetIndex(currentStepIndex, currentStep.isCustomStep);
    this.onNavigateToStepSubject.next(targetIndex);
    this.handleCustomStepNavigation(currentStep);
  }

  public nextStep(): void {
    if (this.selectedIndex() === this.progressSteps.length - 1) return;
    const currentStepIndex = this.findCurrentStepIndex();
    const nextStep = this.fullStepSet[currentStepIndex + 1];
    nextStep.isCustomStep ? this.handleCustomStepNavigation(nextStep) : this.nextSubject.next();
  }

  public previousStep(): void {
    if (this.selectedIndex() === 0) return;
    const currentStepIndex = this.findCurrentStepIndex();
    const previousStep = this.fullStepSet[currentStepIndex - 1];
    const isCurrentStepCustom = this.fullStepSet[currentStepIndex].isCustomStep;
    if (!isCurrentStepCustom) this.previousSubject.next();
    this.handleCustomStepNavigation(previousStep);
  }

  public navigateToStepByURL(url: string, fragment?: string): void {
    const path = url.split('?')[0];

    const targetStep = this.fullStepSet.find((step) => step.route === path);
    const targetStepIndex = this.fullStepSet.findIndex((step) => step.route === path);
    if (!targetStep) return;
    const targetUrl = this.fullStepSet[targetStep.isCustomStep ? targetStepIndex - 1 : targetStepIndex].route;
    const targetIndex = this.progressSteps.findIndex((step) => step.route === targetUrl);
    this.onNavigateToStepSubject.next(targetIndex);
    this.handleCustomStepNavigation(targetStep, fragment);
  }

  public findCurrentStepIndex(): number {
    const currentRoute = this.router.url.split('?')[0];
    return this.fullStepSet.findIndex((step) => step.route === currentRoute);
  }

  public isLastStep() {
    return this.selectedIndex() === this.progressSteps.length - 1;
  }

  protected handleCustomStepNavigation(targetStep: IProgressStep, fragment?: string): void {
    this.navigationService.navigate([targetStep.route], { fragment, queryParamsHandling: 'merge' });
    this.updateProgress(targetStep.progress);
  }

  private calculateTargetIndex(currentStepIndex: number, isCurrentStepCustom: boolean): number {
    const targetIndex = isCurrentStepCustom ? currentStepIndex - 1 : currentStepIndex;
    return this.progressSteps.findIndex((step) => step.route === this.fullStepSet[targetIndex].route);
  }

  private updateCompletedSteps(): void {
    this.progressSteps.forEach((step, index) => {
      step.isCompleted = index < this.selectedIndex();
    });
  }
}
